// SAL6010_R01 목록 테이블(헤더 리스트)
// - 견적서 헤더 목록 표시/선택/컬럼상태 저장/합계 표시를 담당합니다.
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { useSAL6010_R01Store } from "./Store_SAL6010_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { defaultSAL6010_R01HeaderDto, SAL6010_R01HeaderDto } from "./Dto_SAL6010_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { cellDateComparator, onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL6010_R01-grid-column-state';
// 숫자 컬럼 목록 (헤더 DTO 키 보장 필요 없으므로 문자열 배열로 유지)
const numberFields: string[] = ['salAmt'];

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL6010_R01HeaderDto> | null>;
	defaultSAL6010_R01HeaderRow: SAL6010_R01HeaderDto;
	handleCreateClick: () => void;
}

export const Table_SAL6010_R01 = (props: Props) => {

	const { gridRef, handleCreateClick, defaultSAL6010_R01HeaderRow } = props;

	const { SAL6010_R01List, 
		selectedSAL6010_R01, setSelectedSAL6010_R01, initSelectedSAL6010_R01,
		columnState, setColumnState, 
		editSAL6010_R01,
		SAL6010_R01DetailList, setSAL6010_R01DetailList,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useSAL6010_R01Store();

	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'salesEstimateNo',
		gridData: SAL6010_R01List,
		defaultGridData: defaultSAL6010_R01HeaderRow,
		selectedRow: selectedSAL6010_R01,
	});

	// 컬럼 표시명 매핑
	const headerNames: Record<string, string> = {
		rowIndex: "",
		compNm: '회사명',
		estimateDt: '견적일자',
		customNm: '거래처명',
		salAmt: '매출금액',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<SAL6010_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '회사명', colId: 'compNm', field: 'compNm', width: 119,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '견적일자', colId: 'estimateDt', field: 'estimateDt', width: 107,
			cellStyle: (p) => getCellStyle(p, 'salDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (p: { data?: SAL6010_R01HeaderDto }) =>
				p.data?.estimateDt ? formatDate(new Date(p.data.estimateDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '거래처', colId: 'customNm', field: 'customNm', width: 180,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '총액', colId: 'totalAmt', field: 'totalAmt', width: 118,
			cellStyle: (p) => getCellStyle(p, 'totalAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
	], [headerNames]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL6010_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL6010_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	// 목록 행 클릭: 선택 토글 및 신규 모드 전환
	const handleRowClick = useCallback((e: RowClickedEvent<SAL6010_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 표와 정보가 같은지 확인 - salesEstimateNo를 우선 사용
		const currentKey = rowData.salesEstimateNo || rowData.salBillNo;
		const selectedKey = selectedSAL6010_R01?.salesEstimateNo || selectedSAL6010_R01?.salBillNo;
		const isSelected = selectedKey === currentKey;
		
		if (isSelected) {
			setSelectedSAL6010_R01(undefined);
			setDetailScrollTop(null);
			handleCreateClick();
		} else {
			setDetailScrollTop(null);
			setSelectedSAL6010_R01(rowData);
		}
	}, [selectedSAL6010_R01, setSelectedSAL6010_R01, setDetailScrollTop, handleCreateClick]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compNm: SAL6010_R01List.length.toLocaleString() + "건",
		totalAmt: SAL6010_R01List.reduce((sum, item) => (sum + (item.totalAmt ?? 0)), 0).toLocaleString(),
		editable: false,
	}]), [SAL6010_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [SAL6010_R01List, selectedSAL6010_R01]);

	return (
		
		<GridContextWrapper<SAL6010_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSAL6010_R01}
			defaultRowData={defaultSAL6010_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SAL6010_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<SAL6010_R01HeaderDto>({
					idKey: 'salesEstimateNo',
					selectedList: selectedSAL6010_R01?.salesEstimateNo ? [selectedSAL6010_R01.salesEstimateNo] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}	

				// Grid_Select 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 오버 상태 이벤트를 위한 핸들러
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 (로컬스토리)	
				setColumnState={setColumnState}			//컬럼 상태 저장
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}
